import ViewModel from '@/ViewModels/ViewModel';
import BindingContextMixin from '@/BindingContextMixin';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import LayoutFactoryTuple from '@/Factories/LayoutFactoryTuple';
import type Factory from '@/Factories/Factory';
import type ControlFactory from '@/Factories/ControlFactory';
import type GroupFactory from '@/Factories/GroupFactory';

import MixinHelper from '@/Utility/MixinHelper';
import Utilities from '@/Utility/Utilities';

export default class Context extends ViewModel implements CoreFrameInterfaces.IBindingContextMixin {

    /**
     * Eine Liste der Items in diesem Kontext
     */
    protected _createdItems: KnockoutObservableArray<GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple>;

    /**
     * Eine Liste mit den vom Kontext erzeugten Factories
     */
    protected  _createdFactories: KnockoutObservableArray<Factory>;


    /* ==============  from BindingContextMixin============== */
    public _registeredDisposables: Array<any> = [];
    public registerDisposable: (disposable: any) => void;
    /* ====================================================== */


    public constructor(params: CoreFrameInterfaces.IContextConstructorParameter) {
        super(params);

        /* ============================== INTERNAL ============================== */
        this._createdItems = (this.observableOrDefault({}) as KnockoutObservableArray<GroupFactoryTuple | ControlFactoryTuple>).extend({ notify: 'always', rateLimit: { timeout: 200, method: 'notifyWhenChangesStop' } });
        this._createdFactories = (this.observableOrDefault([]) as KnockoutObservableArray<Factory>).extend({ notify: 'always', rateLimit: { timeout: 200, method: 'notifyWhenChangesStop' } });

        /* ============================== PUBLIC ============================== */
        this.isDisposable = this.paramOrDefault(true);
        this.undisposeProperties.push('viewModel');

        /* ======================== TS/JS Inheritance ========================= */
        this.setCreatedTuple = Context.prototype.setCreatedTuple;
        this.getCreatedTuples = Context.prototype.getCreatedTuples;
        this.getCreatedTuple = Context.prototype.getCreatedTuple;
        this.getCreatedGroups = Context.prototype.getCreatedGroups;
        this.getCreatedItems = Context.prototype.getCreatedItems;
        this.setCreatedFactory = Context.prototype.setCreatedFactory;
        this.getCreatedFactories = Context.prototype.getCreatedFactories;
        this.getUIFactory = Context.prototype.getUIFactory;
    }

    /**
     * Registriert ein erzeugtes Tuple im Context
     * @param {string} name - Der Name des Items
     * @param {FactoryTuple} tuple - Das erzeugte Control/GroupFactoryTuple 
     */
    public setCreatedTuple(name: string, tuple: GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple): void {
        if (name) {
            const createdItems = this._createdItems();

            createdItems[name] = tuple;
            this._createdItems(createdItems);
        }
    }

    /**
     * Liefert Tuples von erzeugten Items
     * @param includeGroups
     * @param includeControls
     */
    private getCreatedTuples(includeGroups: boolean, includeControls): Array<GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple> {
        const result = [];
        const createdTuples = this._createdItems();

        for (let i in createdTuples) {
            if (createdTuples.hasOwnProperty(i)) {
                const tuple = createdTuples[i];

                if (tuple instanceof GroupFactoryTuple && includeGroups) {
                    result.push(tuple);
                }

                if (tuple instanceof ControlFactoryTuple && includeControls) {
                    result.push(tuple);
                }
            }
        }

        return result;
    }

    /**
     * Liefert ein im Kontext erzeugtes Tuple
     * @param {string} name - Der Name des Items
     */
    public getCreatedTuple(name: string): GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple | Array<GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple> {
        const createdItems = this._createdItems();

        if (Utilities.isNullOrUndefinedOrBlank(name))
            return null;

        if (name.indexOf('*') > -1) {
            let result: Array<GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple> = [];
            const regex = new RegExp(`^${name.replace('*', '.*')}$`);

            for (let i in createdItems) {
                if (createdItems.hasOwnProperty(i)) {
                    const matches = regex.test(i);

                    if (matches) {
                        result.push(createdItems[i]);
                    }
                }
            }

            return result;
        }

        return createdItems[name];
    }

    /**
     * Liefert alle im Kontext erzeugten Group-Factory-Tuples
     */
    public getCreatedGroups(): GroupFactoryTuple[] {
        return this.getCreatedTuples(true, false) as GroupFactoryTuple[];
    }

    /**
     * Liefert alle im Kontext erzeugten Control-Factory-Tuples
     */
    public getCreatedItems(): ControlFactoryTuple[] {
        return this.getCreatedTuples(false, true) as ControlFactoryTuple[];
    }

    /**
     * Registriert eine erzeugte Factory im Kontext
     */
    public setCreatedFactory(factory: Factory): void {
        this._createdFactories.push(factory);
    }

    /**
    * Liefert die vom Kontext erzeugten Factories
    */
    public getCreatedFactories(): Factory[] {
        return this._createdFactories();
    }

    /**
     * Liefert die vom Kontext erzeugten ControlFactories
     */
    public getCreatedControlFactories(): ControlFactory[] {
        return this.getCreatedFactories().filter(f => f.isControlFactory) as ControlFactory[];
        // TODO: Nach TS-Portierung ist diese L�sung besser:
        // return this.getCreatedFactories().filter(f => f instanceof ControlFactory) as ControlFactory[];
    }

    /**
    * Liefert die vom Kontext erzeugten ControlFactories
    */
    public getCreatedGroupFactories(): GroupFactory[] {
        return this.getCreatedFactories().filter(f => f.isGroupFactory) as GroupFactory[];
        // TODO: Nach TS-Portierung ist diese L�sung besser:
        // return this.getCreatedFactories().filter(f => f instanceof GroupFactory) as GroupFactory[];
    }

    /**
     * Liefert eine im Kontext erzeugte Factory
     */
    public getUIFactory<T extends Factory>(name: string): T {
        const tuple = this.getCreatedTuple(name) as GroupFactoryTuple | ControlFactoryTuple | LayoutFactoryTuple;

        if (tuple != null) {
            return tuple.factory as any;
        }

        return null;
    }

    /**
     * Liefert ein im Kontext erzeugtes Control
     */
    public getUIControl(name: string): JQuery<HTMLElement> {
        const tuple = this.getCreatedTuple(name);

        if (tuple != null && tuple instanceof ControlFactoryTuple) {
            return tuple.control;
        }

        return null;
    }
}

MixinHelper.applyMixins(Context, [BindingContextMixin]);